import type { ActionType } from 'typesafe-actions';
import { put, select, take, takeEvery } from 'redux-saga/effects';

import { IKnoOrganisationFunction } from 'APIPackage/AccountModule/interfaces/IKnoOrganisationFunction';
import { IPageChecklistTemplate } from 'APIPackage/AccountModule/interfaces/IPageChecklistTemplate';
import { IPageRequirementsTemplate } from 'APIPackage/AccountModule/interfaces/IPageRequirementsTemplate';
import { IUserOrganisationsResponse } from 'APIPackage/AccountModule/interfaces/IUserOrganisationsResponse';
import {
  fetchChecklistTemplates,
  IFetchChecklistTemplatesParams,
} from 'APIPackage/AccountModule/services/checklistTemplatesApi';
import {
  fetchRequirementTemplates,
  IFetchRequirementTemplatesParams,
} from 'APIPackage/AccountModule/services/requirementTemplatesApi';
import { fetchKnoOrganisationFunctions, fetchUserOrganisations } from 'APIPackage/AccountModule/services/userApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  accountChecklistTemplatesGet,
  accountChecklistTemplatesGetFailure,
  accountChecklistTemplatesGetRequest,
  accountChecklistTemplatesGetSuccess,
  accountKnoOrganizationFunctionsGet,
  accountKnoOrganizationFunctionsGetFailure,
  accountKnoOrganizationFunctionsGetRequest,
  accountKnoOrganizationFunctionsGetSuccess,
  accountOrganizationsGetFailure,
  accountOrganizationsGetRequest,
  accountOrganizationsGetSuccess,
  accountOrganizationsWithDistrictGetFailure,
  accountOrganizationsWithDistrictGetRequest,
  accountOrganizationsWithDistrictGetSuccess,
  accountRequirementTemplatesGet,
  accountRequirementTemplatesGetFailure,
  accountRequirementTemplatesGetRequest,
  accountRequirementTemplatesGetSuccess,
} from '../actions/accountActions';
import { AccountActionsEnum } from '../enums/AccountActionsEnum';
import { AccountStatusesEnum } from '../enums/AccountStatusesEnum';
import { IAccountOrganizations } from '../interfaces/IAccountOrganizations';
import {
  getAccountChecklistTemplatesItemStatus,
  getAccountKnoOrganizationFunctionsGroupStatus,
  getAccountOrganizationsData,
  getAccountOrganizationsIsLoading,
  getAccountOrganizationsStatus,
  getAccountOrganizationsWithDistrictData,
  getAccountOrganizationsWithDistrictIsLoading,
  getAccountOrganizationsWithDistrictStatus,
  getAccountRequirementTemplatesItemStatus,
} from '../selectors/accountSelectors';

export function* watchAccountSaga() {
  yield takeEvery(AccountActionsEnum.ACCOUNT_CHECKLIST_TEMPLATES_GET, accountChecklistTemplatesGetHandler);
  yield takeEvery(AccountActionsEnum.ACCOUNT_REQUIREMENT_TEMPLATES_GET, accountRequirementTemplatesGetHandler);
  yield takeEvery(
    AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET,
    accountUserOrganizationsWithDistrictGetHandler,
  );
  yield takeEvery(AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET, accountUserOrganizationsGetHandler);
  yield takeEvery(AccountActionsEnum.ACCOUNT_KNO_ORGANIZATION_FUNCTIONS_GET, accountKnoOrganizationFunctionsGetHandler);
}

function* accountChecklistTemplatesGetHandler(action: ActionType<typeof accountChecklistTemplatesGet>) {
  const { params } = action.payload;
  const { isCaching, ...rest } = params;
  const status: AccountStatusesEnum | undefined = yield select(getAccountChecklistTemplatesItemStatus, rest);

  if (!isCaching || !status || status === AccountStatusesEnum.IDLE || status === AccountStatusesEnum.FAILED) {
    yield fetchChecklistTemplatesSaga(rest);
  }
}

function* fetchChecklistTemplatesSaga(params: IFetchChecklistTemplatesParams) {
  function* funcTry() {
    yield put(accountChecklistTemplatesGetRequest(params));
    const checklistTemplatesData: IPageChecklistTemplate = yield fetchChecklistTemplates(params);

    yield put(accountChecklistTemplatesGetSuccess(params, checklistTemplatesData));
  }
  function* funcCatch() {
    yield put(accountChecklistTemplatesGetFailure(params));
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* accountRequirementTemplatesGetHandler(action: ActionType<typeof accountRequirementTemplatesGet>) {
  const { params } = action.payload;
  const { isCaching, ...rest } = params;
  const status: AccountStatusesEnum | undefined = yield select(getAccountRequirementTemplatesItemStatus, rest);

  if (!isCaching || !status || status === AccountStatusesEnum.IDLE || status === AccountStatusesEnum.FAILED) {
    yield fetchRequirementTemplatesSaga(rest);
  }
}

function* fetchRequirementTemplatesSaga(params: IFetchRequirementTemplatesParams) {
  function* funcTry() {
    yield put(accountRequirementTemplatesGetRequest(params));
    const requirementTemplatesData: IPageRequirementsTemplate = yield fetchRequirementTemplates(params);

    yield put(accountRequirementTemplatesGetSuccess(params, requirementTemplatesData));
  }
  function* funcCatch() {
    yield put(accountRequirementTemplatesGetFailure(params));
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

type IAccountOrganizationsData = IAccountOrganizations['data'];

function toUserOrganisationsResponse(data: IAccountOrganizationsData): IUserOrganisationsResponse {
  return { KNO: data.kno, PROSECUTOR: data.prosecutor };
}

function* accountUserOrganizationsWithDistrictGetHandler() {
  const isLoading: boolean = yield select(getAccountOrganizationsWithDistrictIsLoading);

  if (isLoading) {
    return;
  }

  const status: AccountStatusesEnum = yield select(getAccountOrganizationsWithDistrictStatus);

  if (status === AccountStatusesEnum.SUCCEEDED) {
    return;
  }

  const organizationsStatus: AccountStatusesEnum = yield select(getAccountOrganizationsStatus);

  if (organizationsStatus === AccountStatusesEnum.SUCCEEDED) {
    const data: IAccountOrganizationsData = yield select(getAccountOrganizationsData);

    yield put(accountOrganizationsWithDistrictGetRequest());
    yield put(accountOrganizationsWithDistrictGetSuccess(toUserOrganisationsResponse(data)));

    return;
  }

  const isOrganizationsLoading: boolean = yield select(getAccountOrganizationsIsLoading);

  if (isOrganizationsLoading) {
    const action: ActionType<
      typeof accountOrganizationsGetSuccess | typeof accountOrganizationsGetFailure
    > = yield take([
      AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_SUCCESS,
      AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_FAILURE,
    ]);

    if (action.type === AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_SUCCESS) {
      yield put(accountOrganizationsWithDistrictGetRequest());
      yield put(accountOrganizationsWithDistrictGetSuccess(action.payload.data));
    } else {
      yield put(accountOrganizationsWithDistrictGetFailure());
    }

    return;
  }

  yield fetchUserOrganizationsWithDistrictSaga();
}

function* accountUserOrganizationsGetHandler() {
  const isLoading: boolean = yield select(getAccountOrganizationsIsLoading);

  if (isLoading) {
    return;
  }

  const status: AccountStatusesEnum = yield select(getAccountOrganizationsStatus);

  if (status === AccountStatusesEnum.SUCCEEDED) {
    return;
  }

  const withDistrictStatus: AccountStatusesEnum = yield select(getAccountOrganizationsWithDistrictStatus);

  if (withDistrictStatus === AccountStatusesEnum.SUCCEEDED) {
    const data: IAccountOrganizationsData = yield select(getAccountOrganizationsWithDistrictData);

    yield put(accountOrganizationsGetRequest());
    yield put(accountOrganizationsGetSuccess(toUserOrganisationsResponse(data)));

    return;
  }

  const withDistrictLoading: boolean = yield select(getAccountOrganizationsWithDistrictIsLoading);

  if (withDistrictLoading) {
    const action: ActionType<
      typeof accountOrganizationsWithDistrictGetSuccess | typeof accountOrganizationsWithDistrictGetFailure
    > = yield take([
      AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_SUCCESS,
      AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_FAILURE,
    ]);

    if (action.type === AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_SUCCESS) {
      yield put(accountOrganizationsGetRequest());
      yield put(accountOrganizationsGetSuccess(action.payload.data));
    } else {
      yield put(accountOrganizationsGetFailure());
    }

    return;
  }

  yield fetchUserOrganizationsSaga();
}

function* fetchUserOrganizationsSaga() {
  function* funcTry() {
    yield put(accountOrganizationsGetRequest());
    const userOrganizations: IUserOrganisationsResponse = yield fetchUserOrganisations();

    yield put(accountOrganizationsGetSuccess(userOrganizations));
  }
  function* funcCatch() {
    yield put(accountOrganizationsGetFailure());
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* fetchUserOrganizationsWithDistrictSaga() {
  function* funcTry() {
    yield put(accountOrganizationsWithDistrictGetRequest());
    const userOrganizations: IUserOrganisationsResponse = yield fetchUserOrganisations();

    yield put(accountOrganizationsWithDistrictGetSuccess(userOrganizations));
  }
  function* funcCatch() {
    yield put(accountOrganizationsWithDistrictGetFailure());
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* accountKnoOrganizationFunctionsGetHandler(action: ActionType<typeof accountKnoOrganizationFunctionsGet>) {
  const { params } = action.payload;
  const { isCaching, knoOrganizationId } = params;
  const status: AccountStatusesEnum = yield select(getAccountKnoOrganizationFunctionsGroupStatus, knoOrganizationId);

  if (!isCaching || !status || status === AccountStatusesEnum.IDLE || status === AccountStatusesEnum.FAILED) {
    yield fetchKnoOrganizationFunctionsSaga(knoOrganizationId);
  }
}

function* fetchKnoOrganizationFunctionsSaga(knoOrganizationId: string) {
  function* funcTry() {
    yield put(accountKnoOrganizationFunctionsGetRequest(knoOrganizationId));
    const knoOrganisationFunctions: IKnoOrganisationFunction[] = yield fetchKnoOrganisationFunctions(
      `${knoOrganizationId}`,
    );

    yield put(accountKnoOrganizationFunctionsGetSuccess(knoOrganizationId, knoOrganisationFunctions));
  }
  function* funcCatch() {
    yield put(accountKnoOrganizationFunctionsGetFailure(knoOrganizationId));
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}
